import { useState } from 'react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      // Use our custom reset password email endpoint
      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/send-reset-password-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.success) {
        setMessage('Se ha enviado un enlace de recuperación a tu email. Revisa tu bandeja de entrada.');
      } else {
        setError('Error al enviar el email de recuperación. Verifica que el email sea correcto.');
        console.error('Error:', result.error);
      }
    } catch (error) {
      setError('Error al procesar la solicitud. Intenta nuevamente.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-xs text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>
            
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/login')}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Volver al login</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] py-12">
        <div className="w-full max-w-md">
          {/* Forgot Password Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-unlock-line text-2xl text-orange-500"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Recuperar Contraseña</h2>
              <p className="text-gray-600">Ingresa tu email para recibir un enlace de recuperación</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <i className="ri-error-warning-fill text-red-500 mr-2"></i>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            {message && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  <span className="text-green-700 text-sm">{message}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400"></i>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Enviar Enlace de Recuperación
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">¿Recordaste tu contraseña?</p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/login')}
                  className="text-orange-500 hover:text-orange-600 font-medium transition-colors cursor-pointer"
                >
                  Volver al login
                </button>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-shield-check-line text-2xl text-green-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Seguro</h3>
                <p className="text-xs text-gray-600">Proceso completamente seguro</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-time-line text-2xl text-blue-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Rápido</h3>
                <p className="text-xs text-gray-600">Recuperación en minutos</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-customer-service-2-line text-2xl text-purple-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Soporte</h3>
                <p className="text-xs text-gray-600">Ayuda cuando la necesites</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}